import { useState } from 'react'
import { debounce } from 'lodash'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { isSameDay, isAfter, differenceInHours } from 'date-fns'
import { useTranslation } from 'react-i18next'

import Dashboard from '../Dashboard'
import { selectUserRole } from '../../reducers/userDataReducer'

import { useEventAttendees } from './hooks/useEventAttendees'
import { AttendeeStatusValue, downloadCSV } from './utils'
import PageHeader from './components/PageHeader'
import TableFilters from './components/TableFilters'
import Table from './components/Table'
import Statictics from './components/Statitics.tsx'
import { useCheckInAttendee } from './hooks/useCheckInAttendee'
import { useEventStatictics } from './hooks/useEventStatistics'
import { useDeleteAttendee } from './hooks/useDeleteAttendee'
import AddAttendeeModal from './modals/AddAttendeeModal'

const ROLES_WITH_ADD_ATTENDEE_PERMISSION = [
  'Administrator',
  'PropertyManager',
  'ShiftManager',
  'Manager',
]

const isUpToSetHoursAgo = (dateToCheck, hours) => {
  const today = new Date()
  const difference = differenceInHours(today, dateToCheck)
  const isInThePast = difference >= 0

  return isInThePast && difference <= hours
}

const Attendees = () => {
  const { t } = useTranslation(['attendees'])
  const { eventId } = useParams()
  const [selectedTab, setSelectedTab] = useState(AttendeeStatusValue.All)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showAddAttendeeModal, setShowAddAttendeeModal] = useState(false)
  const userRole = useSelector(selectUserRole)

  const { fetchData, data, isLoading, isError } = useEventAttendees({
    eventId: eventId,
    filter: selectedTab,
    search: debouncedSearch,
  })

  const {
    data: statictics,
    isLoading: isStatisticsLoading,
    isError: isStatisticsError,
    fetchData: fetchStatistics,
  } = useEventStatictics({ eventId })

  const { checkInAttendee, isLoading: isCheckInLoading } = useCheckInAttendee()
  const { deleteAttendee, isLoading: isDeleteLoading } = useDeleteAttendee()
  const isLoadingState = isLoading || isCheckInLoading || isDeleteLoading
  const { capacity, date, duration, name, attendees } = data

  const eventDurationInMiliseconds = duration * 60 * 1000

  const isEventInTheFuture = isAfter(new Date(date), new Date())
  const isEventToday = isSameDay(new Date(date), new Date())
  const isTwoDaysAgo = isUpToSetHoursAgo(date, 48)
  const canCheckInAnytime = data?.buildingId === 724
  const isDefaultTicketFree = data?.tickets?.[0].price === 0
  const numberOfNotRemovedAttendees = attendees.filter((attendee) => !attendee.isRemoved)?.length

  const canAddAttendee =
    ROLES_WITH_ADD_ATTENDEE_PERMISSION.includes(userRole) &&
    numberOfNotRemovedAttendees < capacity &&
    isDefaultTicketFree &&
    (isEventInTheFuture || isTwoDaysAgo)

  const canCheckInAttendee = canCheckInAnytime || isEventToday || isTwoDaysAgo

  const setDebouncedSearchTerm = debounce((searchTerm) => {
    setDebouncedSearch(searchTerm)
  }, 500)

  const handleSearchChange = (searchTerm) => {
    setSearch(searchTerm)
    setDebouncedSearchTerm(searchTerm)
  }

  const handleDownloadCSVClick = () => {
    if (!attendees.length) {
      return
    }
    downloadCSV(data.attendees, t)
  }

  const handleDeleteAttendee = async (attendeeId, attendeeRole, withRefund) => {
    try {
      await deleteAttendee(attendeeId, eventId, attendeeRole, withRefund)
      fetchData()
      fetchStatistics()
    } catch (error) {
      console.error(error)
    }
  }

  const handleCheckinAttendee = async (attendeeIds, guestsIds) => {
    try {
      await checkInAttendee(attendeeIds, guestsIds, eventId)
      fetchData()
      fetchStatistics()
    } catch (error) {
      console.error(error)
    }
  }

  const onAddAttendeeSuccess = () => {
    fetchStatistics()
    fetchData()
  }

  return (
    <Dashboard.Container className="ctp-event-attendees">
      <PageHeader
        eventName={name}
        eventStartTime={date}
        eventEndTime={date + eventDurationInMiliseconds}
      />
      <Statictics
        isLoading={isStatisticsLoading}
        isError={isStatisticsError}
        totalSingedUp={statictics?.totalStats?.signedUp}
        totalStats={statictics.totalStats}
        groupedStats={statictics.groupedStats}
      />
      <div className="attendees_table-card">
        <TableFilters
          search={search}
          onSearchChange={handleSearchChange}
          selectedTab={selectedTab}
          onSelectedTab={setSelectedTab}
          onDownloadClick={handleDownloadCSVClick}
          onAddAttendee={() => setShowAddAttendeeModal(true)}
          canAddAttendee={canAddAttendee}
        />
        <Table
          attendees={attendees}
          isLoading={isLoadingState}
          isError={isError}
          canCheckInAttendee={canCheckInAttendee}
          onDeleteAttendee={handleDeleteAttendee}
          onCheckInAttendee={handleCheckinAttendee}
        />
      </div>
      <AddAttendeeModal
        isVisible={showAddAttendeeModal}
        onClose={() => setShowAddAttendeeModal(false)}
        eventId={eventId}
        onAddSuccess={onAddAttendeeSuccess}
        eventBuildingId={data?.buildingId}
      />
    </Dashboard.Container>
  )
}

export default Attendees
